// Mock data for the brand setup screen. The form starts empty; the preview
// falls back to `previewSample` for anything still blank, so the document on
// the right always reads as a finished page instead of a skeleton.

import { BrandDetails } from "../types/brand.types";

export const emptyBrandDetails: BrandDetails = {
  fullName: "",
  brandName: "",
  logo: null,
  phone: "",
  whatsapp: "",
  whatsappSameAsPhone: false,
  email: "",
  address: "",
  state: "",
  city: "",
  pincode: "",
  accountHolder: "",
  accountNumber: "",
  bankName: "",
  ifsc: "",
  upiNumber: "",
  upiId: "",
  paymentQr: null,
};

/** Upload rules, quoted in the hint under each picker and enforced by it. */
export const MAX_UPLOAD_BYTES = 1024 * 1024;

/**
 * Longest side, in pixels. A logo or a QR never needs to be this big, and a
 * camera-roll photo dropped in by mistake usually is — the ceiling is what
 * stops one being carried into every invoice and meal plan.
 */
export const MAX_IMAGE_DIMENSION = 4096;

/**
 * Raster only. SVG is deliberately not on the list: it is an XML document
 * rather than an image, it can carry `<script>`, and these files are served
 * to clients — so it is rejected however it is offered.
 *
 * `image/jpg` is not a real media type, but some Windows registries hand it to
 * the file picker for a `.jpg`; it is the same JPEG either way, and leaving it
 * off would turn away valid files.
 */
export const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];
export const ACCEPTED_IMAGE_ATTR = ".png,.jpg,.jpeg,.webp";

/** Stand-ins used by the preview until the matching field is filled in. */
export const previewSample = {
  fullName: "Riya Roy",
  brandName: "Nutro diet studio",
  /** The slug form, which is what the public page address is built from. */
  handle: "riya",
  phone: "+91 9876543210",
  email: "hello@dietstudion.com",
  address: "102, Skylark, Andheri West,",
  cityLine: "Mumbai Maharashtra, 40064",
  /** The same person as `fullName` — an account is held in a name, not a
   *  brand, and a specimen that split the two would teach the wrong thing. */
  accountHolder: "Riya Roy",
  accountNumber: "5648920272",
  bankName: "State bank of India",
  ifsc: "SBIN00001",
  upiNumber: "+91 9876543210",
  upiId: "9876543210@kotakbank",
};

/** The invoice preview is a specimen document, not a real bill. */
export const invoiceSample = {
  number: "#06/002",
  dateIssued: "5 July 2026",
  billedTo: {
    name: "Ajay Kumar",
    address: "1204, Galaxy, SV Road Malad West,",
    cityLine: "Mumbai Maharashtra, 40064",
  },
  lineItem: {
    planName: "Weight loss plan",
    duration: "1 month",
    amount: "₹1499",
  },
  payableAmount: "₹ 1499",
};

/** One food on an option, with the amount printed as the PDF prints it —
 *  the serving, and the weight it comes to in brackets after it. */
export interface MealSpecimenItem {
  food: string;
  amount: string;
}

/**
 * One option card: the foods on it and what they come to. Whole numbers, as
 * the PDF prints them — a meal plan is read in whole grams. The specimen
 * figures add up at 4/4/9 kcal a gram, so a reader who checks one against
 * the macros beside it finds what a generated plan would give them.
 */
export interface MealSpecimenOption {
  items: MealSpecimenItem[];
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

/** One meal, which is one sheet of the document. */
export interface MealSpecimen {
  label: string;
  /** Printed as the PDF prints it — "08:30 am". */
  time: string;
  options: MealSpecimenOption[];
}

/**
 * The meal plan preview's specimen document, the way `invoiceSample` is the
 * invoice's: a client, a plan code and three meals standing in for whatever
 * plan the PDF is generated from. Not a plan anybody holds, and never sent —
 * the generated document reads the plan's own stored rows.
 */
export const mealPlanSample = {
  clientName: "Ajay Kumar",
  planId: "NMP004",
  /**
   * "Created on", not "Start date". A plan has no start date — the
   * engagement that would carry one does not exist — and the PDF prints the
   * created date under this label rather than inventing a clinical fact the
   * document cannot know. See `meal_plan.html`.
   */
  createdOn: "01 July, 2026",
  goal: "Weight loss",
  // Everyday meals rather than any plan a practitioner has written: the sheet
  // is here to show what a meal looks like printed, and a reader recognising
  // the food is what lets them read past it to the layout. A meal with two
  // options and meals with one, so the card stack and the "1 meal option"
  // line are both shown as they come out.
  meals: [
    {
      label: "Breakfast",
      time: "08:30 am",
      options: [
        {
          items: [
            { food: "Oats porridge", amount: "1 Bowl (medium) (200 gm)" },
            { food: "Banana", amount: "1 Piece (medium) (100 gm)" },
          ],
          kcal: 309,
          protein: 9,
          carbs: 57,
          fat: 5,
        },
        {
          items: [
            { food: "Vegetable poha", amount: "1 Cup (medium) (150 gm)" },
            { food: "Buttermilk", amount: "200 ml" },
          ],
          kcal: 242,
          protein: 7,
          carbs: 40,
          fat: 6,
        },
      ],
    },
    {
      label: "Lunch",
      time: "01:30 pm",
      options: [
        {
          items: [
            { food: "Chapati", amount: "2 Piece (medium) (80 gm)" },
            { food: "Mixed vegetable sabzi", amount: "1 Cup (small) (100 gm)" },
            { food: "Curd", amount: "1 Katori (small) (100 gm)" },
          ],
          kcal: 412,
          protein: 16,
          carbs: 60,
          fat: 12,
        },
      ],
    },
    {
      label: "Dinner",
      time: "08:30 pm",
      options: [
        {
          items: [
            { food: "Moong dal khichdi", amount: "1 Bowl (medium) (200 gm)" },
            { food: "Cucumber salad", amount: "1 Cup (small) (100 gm)" },
          ],
          kcal: 315,
          protein: 13,
          carbs: 50,
          fat: 7,
        },
      ],
    },
  ] as MealSpecimen[],
};
